import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { config } from "@/config";
import { SettingsApi } from "@/api/giftcard.api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CreditCard,
  TrendingUp,
  Users,
  Wallet,
  XCircle,
  Plus,
  CheckCircle,
  Clock,
  Percent,
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend as RechartsLegend,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface MainCard {
  issuedCard: string;
  issuedCardValue: string;
  redeemedCard: string;
  redeemedValue: string | null;
  expiredCardValue: string | null;
}

interface DashboardResponse {
  mainCards: MainCard[];
  issuanceTrends: Array<{ month: string; issuedCards: string }>;
  redemptionTrends: Array<{ month: string; redeemedCards: string }>;
  giftCardStatus: Array<{
    issued?: string;
    redeemed?: string;
    expired?: string;
    giftCardStatus?: string;
    statusCount?: string;
  }>;
  recentActivity: Array<{
    issuedCardCount: string;
    redeemedCardCount: string;
    expiringCardCount: string;
    partiallyRedeemedCardCount: string;
  }>;
  currency?: {
    currency: string;
  };
  error_msg: string;
}

const GiftCardDashboard = () => {
  const [dashboardData, setDashboardData] = useState<MainCard | null>(null);
  const [issuanceTrends, setIssuanceTrends] = useState<
    Array<{ month: string; issuedCards: string }>
  >([]);
  const [redemptionTrends, setRedemptionTrends] = useState<
    Array<{ month: string; redeemedCards: string }>
  >([]);
  const [recentActivity, setRecentActivity] = useState<{
    issuedCardCount: string;
    redeemedCardCount: string;
    expiringCardCount: string;
    partiallyRedeemedCardCount: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusSummary, setStatusSummary] = useState<{
    issued: number;
    redeemed: number;
    expired: number;
  } | null>(null);
  const [malls, setMalls] = useState<Array<{ id: number; name: string }>>([]);
  const [selectedMallId, setSelectedMallId] = useState<number | null>(null);
  const [isMallLoading, setIsMallLoading] = useState(false);
  const [currency, setCurrency] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const fetchDashboardData = useCallback(
    async (
      mallIdParam: number,
      fromDateParam?: string,
      toDateParam?: string
    ) => {
      setIsLoading(true);
      try {
        // Prepare payload
        const payload: {
          p_MallId: number;
          p_MerchantCode: string;
          p_FromDate: string | null;
          p_ToDate: string | null;
        } = {
          p_MallId: mallIdParam,
          p_MerchantCode: "",
          p_FromDate: fromDateParam
            ? new Date(fromDateParam + "T00:00:00.000Z").toISOString()
            : null,
          p_ToDate: toDateParam
            ? new Date(toDateParam + "T23:59:59.999Z").toISOString()
            : null,
        };

        const response = await axios.post<DashboardResponse>(
          `${config.GIFT_API_BASE_URL}/GiftCardDashboard/GetGiftCardDashboardDetails`,
          payload,
          {
            headers: {
              XApiKey: config.GIFT_API_KEY,
              "Content-Type": "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.mainCards &&
          response.data.mainCards.length > 0
        ) {
          const rawMain = response.data.mainCards[0] as MainCard & {
            issuedcard?: string;
          };
          setDashboardData({
            issuedCard: rawMain.issuedCard ?? rawMain.issuedcard ?? "0",
            issuedCardValue: rawMain.issuedCardValue ?? "0",
            redeemedCard: rawMain.redeemedCard ?? "0",
            redeemedValue: rawMain.redeemedValue ?? null,
            expiredCardValue: rawMain.expiredCardValue ?? null,
          });
          setIssuanceTrends(response.data.issuanceTrends || []);
          setRedemptionTrends(response.data.redemptionTrends || []);
          if (
            response.data.recentActivity &&
            response.data.recentActivity.length > 0
          ) {
            setRecentActivity(response.data.recentActivity[0]);
          } else {
            setRecentActivity(null);
          }

          // Parse giftCardStatus summary if provided as first object with issued/redeemed/expired
          if (
            response.data.giftCardStatus &&
            Array.isArray(response.data.giftCardStatus) &&
            response.data.giftCardStatus.length > 0
          ) {
            const first = response.data.giftCardStatus[0] as any;
            if (
              first &&
              (typeof first.issued !== "undefined" ||
                typeof first.redeemed !== "undefined" ||
                typeof first.expired !== "undefined")
            ) {
              setStatusSummary({
                issued: parseFloat(first.issued ?? 0) || 0,
                redeemed: parseFloat(first.redeemed ?? 0) || 0,
                expired: parseFloat(first.expired ?? 0) || 0,
              });
            } else {
              setStatusSummary(null);
            }
          } else {
            setStatusSummary(null);
          }

          // Save currency to localStorage and state
          if (response.data.currency?.currency) {
            const currencyCode = response.data.currency.currency;
            localStorage.setItem("giftCardCurrency", currencyCode);
            setCurrency(currencyCode);
          } else {
            // Try to get from localStorage if not in response
            const storedCurrency = localStorage.getItem("giftCardCurrency");
            if (storedCurrency) {
              setCurrency(storedCurrency);
            }
          }
        } else {
          toast.error("No data available");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Failed to load dashboard data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const fetchMallOptions = useCallback(async () => {
    setIsMallLoading(true);
    try {
      const data = await SettingsApi.getMallDetails();
      const mallsData = Array.isArray(data?.malldetails)
        ? data.malldetails
        : [];
      const mapped = mallsData.map((mall: any) => ({
        id: mall.mallId,
        name: mall.mallName || `Mall ${mall.mallId}`,
      }));
      setMalls(mapped);
      if (mapped.length > 0) {
        setSelectedMallId(mapped[0].id);
      } else {
        setSelectedMallId(null);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching malls:", error);
      setMalls([]);
      setSelectedMallId(null);
      setIsLoading(false);
      toast.error("Failed to load mall list");
    } finally {
      setIsMallLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMallOptions();
  }, [fetchMallOptions]);

  useEffect(() => {
    // Load currency from localStorage on mount
    const storedCurrency = localStorage.getItem("giftCardCurrency");
    if (storedCurrency) {
      setCurrency(storedCurrency);
    }
  }, []);

  useEffect(() => {
    if (selectedMallId !== null) {
      // Only fetch when mall changes, not when dates change
      fetchDashboardData(selectedMallId, fromDate, toDate);
    }
  }, [selectedMallId, fetchDashboardData]);

  // Validation handlers
  const handleFromDateChange = (value: string) => {
    setFromDate(value);
    // If fromDate is cleared, also clear toDate
    if (!value) {
      setToDate("");
      return;
    }
    // If toDate is set and is strictly less than fromDate, clear it
    // (same-day ranges are allowed)
    if (toDate && new Date(toDate) < new Date(value)) {
      setToDate("");
      toast.error("To date must be same as or after from date");
    }
  };

  const handleToDateChange = (value: string) => {
    if (!fromDate) {
      toast.error("Please select from date first");
      return;
    }
    // Allow same-day ranges: toDate can be equal to fromDate
    if (value && new Date(value) < new Date(fromDate)) {
      toast.error("To date must be same as or after from date");
      return;
    }
    setToDate(value);
  };

  // Apply button handler
  const handleApply = () => {
    if (!fromDate) {
      toast.error("Please select from date first");
      return;
    }
    if (!toDate) {
      toast.error("Please select to date");
      return;
    }
    if (selectedMallId !== null) {
      fetchDashboardData(selectedMallId, fromDate, toDate);
    } else {
      toast.error("Please select a mall first");
    }
  };

  // Reset button handler
  const handleReset = () => {
    setFromDate("");
    setToDate("");
    // Fetch data without date filters
    if (selectedMallId !== null) {
      fetchDashboardData(selectedMallId);
    }
  };

  // All 12 months array
  const allMonths = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Prepare issuance trends data with all 12 months
  const getIssuanceChartData = () => {
    return allMonths.map((month) => {
      const trend = issuanceTrends.find(
        (t) => t.month.toLowerCase() === month.toLowerCase()
      );
      return {
        month: month.substring(0, 3), // Short month name
        issuedCards: trend ? parseInt(trend.issuedCards || "0", 10) : 0,
      };
    });
  };

  // Prepare redemption trends data with all 12 months
  const getRedemptionChartData = () => {
    return allMonths.map((month) => {
      const trend = redemptionTrends.find(
        (t) => t.month.toLowerCase() === month.toLowerCase()
      );
      return {
        month: month.substring(0, 3), // Short month name
        redeemedCards: trend ? parseInt(trend.redeemedCards || "0", 10) : 0,
      };
    });
  };

  const formatNumber = (value: string) => {
    return parseInt(value || "0", 10).toLocaleString();
  };

  const formatCurrency = (value: string | null) => {
    if (!value || value === "null" || value === null) {
      const currencyCode =
        currency || localStorage.getItem("giftCardCurrency") || "USD";
      return `0 ${currencyCode}`;
    }
    const amount = parseInt(value || "0", 10).toLocaleString();
    const currencyCode =
      currency || localStorage.getItem("giftCardCurrency") || "USD";
    return `${amount} ${currencyCode}`;
  };

  const formatCount = (value: string) => {
    const num = parseFloat(value || "0");
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const PIE_COLORS = ["#F59E0B", "#10b981", "#9ca3af"]; // issued, redeemed, expired

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Manage your digital gift card system
            </p>
          </div>
          <div className="w-full lg:w-64">
            <Select
              value={
                selectedMallId !== null ? selectedMallId.toString() : undefined
              }
              onValueChange={(value) => setSelectedMallId(Number(value))}
              disabled={isMallLoading || malls.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    isMallLoading ? "Loading malls..." : "Select mall"
                  }
                />
              </SelectTrigger>
              <SelectContent className="!max-h-48 overflow-auto py-0">
                {malls.length > 0 ? (
                  malls.map((mall) => (
                    <SelectItem key={mall.id} value={mall.id.toString()}>
                      {mall.name}
                    </SelectItem>
                  ))
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    {isMallLoading ? "Loading..." : "No malls found"}
                  </div>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">
              Dashboard metrics are filtered by mall.
            </p>
          </div>
        </div>

        {/* Date Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-end">
              <div className="flex-1 w-full sm:w-auto">
                <Label htmlFor="fromDate" className="mb-2 block">
                  From Date *
                </Label>
                <Input
                  id="fromDate"
                  type="date"
                  value={fromDate}
                  onChange={(e) => handleFromDateChange(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="flex-1 w-full sm:w-auto">
                <Label htmlFor="toDate" className="mb-2 block">
                  To Date *
                </Label>
                <Input
                  id="toDate"
                  type="date"
                  value={toDate}
                  onChange={(e) => handleToDateChange(e.target.value)}
                  min={fromDate || undefined}
                  disabled={!fromDate}
                  className="w-full"
                />
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  onClick={handleApply}
                  disabled={!fromDate || !toDate || isLoading}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Apply
                </Button>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  disabled={isLoading}
                >
                  Reset
                </Button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Select date range to filter dashboard data. From date must be
              selected first. Click Apply to filter or Reset to clear dates.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 py-3 pb-1">
                <CardTitle className="text-xs font-medium text-gray-500 animate-pulse bg-gray-200 h-3 w-20 rounded"></CardTitle>
                <div className="h-3.5 w-3.5 bg-gray-200 rounded animate-pulse"></div>
              </CardHeader>
              <CardContent className="px-4 pb-3 pt-1">
                <div className="text-lg font-semibold animate-pulse bg-gray-200 h-6 w-16 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : dashboardData ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 py-3 pb-1">
              <CardTitle className="text-xs font-medium text-gray-500">
                Issued Cards
              </CardTitle>
              <CreditCard className="h-3.5 w-3.5 text-gray-400" />
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-1">
              <div className="text-lg font-semibold text-gray-900 tabular-nums tracking-tight">
                {formatNumber(dashboardData.issuedCard)}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 py-3 pb-1">
              <CardTitle className="text-xs font-medium text-gray-500">
                Issued Card Value
              </CardTitle>
              <Wallet className="h-3.5 w-3.5 text-gray-400" />
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-1">
              <div className="text-lg font-semibold text-gray-900 tabular-nums tracking-tight">
                {formatCurrency(dashboardData.issuedCardValue)}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 py-3 pb-1">
              <CardTitle className="text-xs font-medium text-gray-500">
                Redeemed Cards
              </CardTitle>
              <Users className="h-3.5 w-3.5 text-gray-400" />
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-1">
              <div className="text-lg font-semibold text-gray-900 tabular-nums tracking-tight">
                {formatNumber(dashboardData.redeemedCard)}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 py-3 pb-1">
              <CardTitle className="text-xs font-medium text-gray-500">
                Redeemed Value
              </CardTitle>
              <TrendingUp className="h-3.5 w-3.5 text-gray-400" />
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-1">
              <div className="text-lg font-semibold text-gray-900 tabular-nums tracking-tight">
                {formatCurrency(dashboardData.redeemedValue)}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 py-3 pb-1">
              <CardTitle className="text-xs font-medium text-gray-500">
                Expired Card Value
              </CardTitle>
              <XCircle className="h-3.5 w-3.5 text-gray-400" />
            </CardHeader>
            <CardContent className="px-4 pb-3 pt-1">
              <div className="text-lg font-semibold text-gray-900 tabular-nums tracking-tight">
                {formatCurrency(dashboardData.expiredCardValue)}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">No data available</div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {isLoading && (
          <>
            {[1, 2, 3, 4].map((item) => (
              <Card key={`chart-skeleton-${item}`}>
                <CardHeader>
                  <div className="h-5 w-32 bg-gray-200 animate-pulse rounded" />
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] w-full bg-gray-100 animate-pulse rounded" />
                </CardContent>
              </Card>
            ))}
          </>
        )}

        {!isLoading && (
          <>
            {/* Recent Activity Card - Full Width */}
            {recentActivity && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>
                    Recent Activity{" "}
                    <span className="text-xs text-gray-500">
                      (Current Month)
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col justify-between h-full min-h-[140px] p-5 border rounded-lg bg-blue-50">
                      <div className="flex items-center space-x-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 text-white">
                          <Plus className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Issued Cards</p>
                          <p className="text-xl font-semibold text-blue-700">
                            {formatCount(recentActivity.issuedCardCount)}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 leading-snug">
                        Total cards issued over the selected period.
                      </p>
                    </div>
                    <div className="flex flex-col justify-between h-full min-h-[140px] p-5 border rounded-lg bg-green-50">
                      <div className="flex items-center space-x-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500 text-white">
                          <CheckCircle className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            Redeemed Cards
                          </p>
                          <p className="text-xl font-semibold text-green-700">
                            {formatCount(recentActivity.redeemedCardCount)}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 leading-snug">
                        Cards successfully redeemed by customers.
                      </p>
                    </div>
                    <div className="flex flex-col justify-between h-full min-h-[140px] p-5 border rounded-lg bg-orange-50">
                      <div className="flex items-center space-x-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500 text-white">
                          <Clock className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            Expiring Cards
                          </p>
                          <p className="text-xl font-semibold text-orange-700">
                            {formatCount(recentActivity.expiringCardCount)}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 leading-snug">
                        Cards approaching expiry that may need attention.
                      </p>
                    </div>
                    <div className="flex flex-col justify-between h-full min-h-[140px] p-5 border rounded-lg bg-purple-50">
                      <div className="flex items-center space-x-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500 text-white">
                          <Percent className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            Partially Redeemed
                          </p>
                          <p className="text-xl font-semibold text-purple-700">
                            {formatCount(
                              recentActivity.partiallyRedeemedCardCount
                            )}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 leading-snug">
                        Cards with a remaining balance after partial use.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Issuance Trends Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Issuance Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ChartContainer
                      config={{
                        issuedCards: {
                          label: "Issued Cards",
                          color: "#F59E0B",
                        },
                      }}
                    >
                      <BarChart data={getIssuanceChartData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          fontSize={12}
                        />
                        <YAxis fontSize={12} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar
                          dataKey="issuedCards"
                          fill="var(--color-issuedCards)"
                        />
                      </BarChart>
                    </ChartContainer>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Redemption Trends Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Redemption Trends</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ChartContainer
                      config={{
                        redeemedCards: {
                          label: "Redeemed Cards",
                          color: "#10b981",
                        },
                      }}
                    >
                      <BarChart data={getRedemptionChartData()}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="month"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          fontSize={12}
                        />
                        <YAxis fontSize={12} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Bar
                          dataKey="redeemedCards"
                          fill="var(--color-redeemedCards)"
                        />
                      </BarChart>
                    </ChartContainer>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default GiftCardDashboard;
